<script setup lang="ts">
import type { SelectedRegionsSectionClick } from '@/components/Sections/SelectedRegionsSection';
import type { ViewMoreClickedPayload } from '~/components/Sections';
import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import type { Property } from '~/core/entities/property';
import type { PaginatedPropertyFilters } from '~/core/entities/property/filters';
import type {
	CardHeroOpened,
	FilterApplied,
	QuickNeighborhoodFilterApplied,
	SectionName
} from '~/types/mixpanel';
import type { NuxtListPropertiesApiReturn } from '~~/server/api/properties/index.get';

import { testimonials } from '@@/mocks/static/testimonials';
import { defaultTwitterCardMeta } from '@/constants/seo';
import { useEvents } from '~/composables/events/useEvents';
import { useHomepageSeo } from '~/composables/seo/useHomepageSeo';
import { useAbsoluteUrl } from '~/composables/useAbsoluteUrl';
import { useHomeShortsRecommendation } from '~/composables/useHomeShortsRecommendation';
import { useMixpanel } from '~/composables/useMixpanel';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt/nuxtPropertiesRepository';

definePageMeta({
	layout: 'default-transparent',
	path: '/'
});

const { hasPreferences, getPreferences } = usePreferencesCookie();

const defaultPropertiesReturn = {
	properties: [] as NuxtListPropertiesApiReturn['properties'],
	pagination: {
		totalItems: 0,
		totalPages: 0,
		currentPage: 1,
		itemsPerPage: 12,
		page: 1,
		perPage: 12,
		filteredCount: 0
	},
	locationSlugFilters: []
} as NuxtListPropertiesApiReturn;

const { toAbsoluteUrl } = useAbsoluteUrl();
const ogImageUrl = toAbsoluteUrl('/images/social/openGraph/og_home.webp');
const { title: homepageTitle, description: homepageDescription } =
	useHomepageSeo();

useSeoMeta({
	title: homepageTitle,
	description: homepageDescription,
	keywords: 'imóveis, alto padrão, PilarHomes, endereço',
	ogSiteName: 'PilarHomes',
	ogTitle: homepageTitle,
	ogDescription: homepageDescription,
	ogImage: ogImageUrl,
	twitterCard: defaultTwitterCardMeta,
	twitterTitle: homepageTitle,
	twitterDescription: homepageDescription,
	twitterImage: ogImageUrl
});
const { user } = useUserSession();

async function getProperties(
	filters: Partial<PaginatedPropertyFilters>
): Promise<NuxtListPropertiesApiReturn | null> {
	try {
		const getCookieData = useCookieData();
		const [result, error] = await nuxtPropertiesRepository.list(
			{
				...filters,
				markUserWishlistsId: user.value?.id
			},
			getCookieData()
		);

		if (error) {
			return defaultPropertiesReturn;
		}

		return result;
	} catch {
		return defaultPropertiesReturn;
	}
}

const { data: exclusiveProperties, status: isExclusiveRequestStatus } =
	useAsyncData<NuxtListPropertiesApiReturn>(
		'home:is-exclusive-properties',
		async () => {
			const properties = await getProperties({
				page: 1,
				perPage: 12,
				isExclusive: true,
				sortBy: 'portal_home_select'
			});

			if (!properties) {
				return defaultPropertiesReturn;
			}

			// Enxuga o payload de hidratação (__NUXT_DATA__): o card da home só
			// renderiza o carrossel de imagens + campos básicos — nunca a descrição
			// do anúncio (~2-3KB/imóvel) nem featuresByCategory. Esvaziá-los tira
			// esse texto pesado (x12) do HTML sem afetar o render.
			return {
				...properties,
				properties: properties.properties.map(property => ({
					...property,
					ad: { ...property.ad, description: '' },
					featuresByCategory: {} as Property['featuresByCategory']
				}))
			};
		},
		{
			default: () => defaultPropertiesReturn,
			lazy: true
		}
	);

const isLoadingExclusiveProperties = computed(() => {
	return isExclusiveRequestStatus.value === 'pending';
});

const { data: selectedProperties, status: selectedPropertiesRequestStatus } =
	useAsyncData<NuxtListPropertiesApiReturn | null>(
		'home:selected-properties',
		async () => {
			const preferences = getPreferences();

			const maxAskingPrice = preferences?.askingPrice
				? Math.ceil(preferences?.askingPrice * 1.15)
				: undefined;

			const properties = await getProperties({
				page: 1,
				perPage: 15,
				regions: (preferences?.regions as string[]) || undefined,
				maxAskingPrice,
				sortBy: 'asking_price',
				orderBy: 'desc',
				excludeFeaturedVideos: true
			});

			if ((properties?.properties?.length ?? 0) < 5) {
				return null;
			}

			return properties;
		},
		{
			default: () => defaultPropertiesReturn,
			lazy: true,
			server: false
		}
	);

/**
 * Loading state for selected properties.
 * Includes 'idle' state to show skeleton immediately when preferences exist,
 * before the fetch even starts on the client.
 */
const isLoadingSelectedProperties = computed(() => {
	const status = selectedPropertiesRequestStatus.value;
	return status === 'idle' || status === 'pending';
});

const ctaLink = computed<string>(() => {
	const preferences = getPreferences();
	const maxAskingPrice = preferences?.askingPrice
		? Math.ceil(preferences?.askingPrice * 1.15)
		: undefined;

	const path = PropertyFiltersPresenter.getPathFromFilters({
		isChosenForYou: true,
		regions: (preferences?.regions as string[]) || undefined,
		states: (preferences?.states as string[]) || ['sp'],
		cities: (preferences?.cities as string[]) || undefined,
		propertyTypes: preferences?.propertyTypes as PropertyTypeEnum[],
		maxAskingPrice
	});

	return path;
});

/**
 * Shows the selected properties section (with skeleton while loading).
 * Only renders on client when user has preferences cookie.
 * Hides entirely if loading finished but has < 5 properties.
 */
const shouldShowSelectedProperties = computed(() => {
	if (!hasPreferences.value) return false;

	// While loading, show skeleton
	if (isLoadingSelectedProperties.value) return true;

	// After loading, only show if has enough properties
	return (selectedProperties.value?.properties?.length ?? 0) >= 5;
});

const testimonialsSectionVariant = 'light';

const {
	trackPageView,
	trackCtaOffMarketClicked,
	trackCtaViewMoreClicked,
	trackCarouselNeighborhoodClicked,
	trackEmptyStateFilter,
	trackFilterApplied,
	trackQuickNeighborhoodFilterApplied,
	trackCardHeroOpened
} = useEvents();

const offMarketBus = useEventBus('cta_off_market_clicked');
const searchSuggestEmptyStateEventBus = useEventBus<string>(
	'search_suggest_empty_state'
);

const offMarketCleanup = onClientEventBus(offMarketBus, () => {
	trackCtaOffMarketClicked({
		[MixpanelProps.CTA_TEXT]: 'Imóveis Off-Market',
		[MixpanelProps.CURRENT_PAGE]: 'Home',
		[MixpanelProps.SECTION_NAME]: 'selected properties',
		[MixpanelProps.DESTINATION_URL]: '/off-market'
	});
});

const searchSuggestCleanup = onClientEventBus(
	searchSuggestEmptyStateEventBus,
	handleSearchSuggestEmptyState
);

function handleViewMoreClicked(
	sectionName: SectionName,
	payload: ViewMoreClickedPayload
) {
	trackCtaViewMoreClicked({
		[MixpanelProps.CTA_TEXT]: payload.ctaText,
		[MixpanelProps.CURRENT_PAGE]: 'Home',
		[MixpanelProps.SECTION_NAME]: sectionName,
		[MixpanelProps.DESTINATION_URL]: payload.href,
		[MixpanelProps.CLICK_COUNT]: payload.clickCount
	});
}

function handleCarouselNeighborhoodClicked({
	clickLocation,
	cardText,
	neighborhoodName,
	destinationUrl
}: SelectedRegionsSectionClick) {
	trackCarouselNeighborhoodClicked({
		[MixpanelProps.CARD_TEXT]: cardText ?? '',
		[MixpanelProps.NEIGHBORHOOD_NAME]: neighborhoodName,
		[MixpanelProps.DESTINATION_URL]: destinationUrl,
		[MixpanelProps.CLICK_NEIGHBORHOOD_TAB]:
			clickLocation === 'neighborhood tab',
		[MixpanelProps.CLICK_NEIGHBORHOOD_CARD]:
			clickLocation === 'neighborhood card',
		[MixpanelProps.CLICK_NEIGHBORHOOD_TITLE]:
			clickLocation === 'neighborhood title',
		[MixpanelProps.CLICK_CARD]: clickLocation === 'card clicked'
	});
}

function handleSearchSuggestEmptyState(searchTerm: string) {
	trackEmptyStateFilter({
		[MixpanelProps.TEXT_SEARCH]: searchTerm
	});
}

function handleFilterApplied(event: FilterApplied) {
	trackFilterApplied(event);
}

function handleQuickNeighborhoodFilterApplied(
	event: QuickNeighborhoodFilterApplied
) {
	trackQuickNeighborhoodFilterApplied(event);
}

function handleCardHeroOpened(event: CardHeroOpened) {
	trackCardHeroOpened(event);
}

const lastViewedProperty = useCookie<string | null | undefined>(
	'last-viewed-property'
);

let pageviewFired = false;
const mixpanel = useMixpanel();

function trackPageViewOnce() {
	if (pageviewFired) return;
	pageviewFired = true;
	trackPageView({
		current_page: 'Home',
		previous_page_url: document.referrer
	});
}

const {
	filters: homeShortsFilters,
	title: homeShortsTitle,
	handleFallbackUpdate: handleHomeShortsFallbackUpdate
} = useHomeShortsRecommendation(lastViewedProperty);

function handleShortsHasVideos(hasVideos: boolean): void {
	trackPageViewOnce();
	mixpanel.trackPageview({
		[MixpanelProps.HAS_SHORTS_SECTION]: hasVideos
	});
}

onUnmounted(() => {
	offMarketCleanup();
	searchSuggestCleanup();
});
</script>

<template>
	<div>
		<div class="relative">
			<!-- Hero is NOT lazy-loaded because it contains the LCP image -->
			<SectionsHeroSectionHero @filter-applied="handleFilterApplied" />
		</div>

		<!-- ValueProposition is NOT lazy-hydrated per requirements -->
		<LazySectionsValueProposition />

		<ClientOnly>
			<SectionsShortsSection
				:title="homeShortsTitle"
				:filters="homeShortsFilters"
				enable-recommendation-type-analytics
				@update:used-fallback="handleHomeShortsFallbackUpdate"
				@update:has-videos="handleShortsHasVideos"
			/>
			<template #fallback>
				<SectionsShortsSectionSkeleton />
			</template>
		</ClientOnly>

		<!-- Client-only because selectedProperties uses server: false -->
		<ClientOnly>
			<LazySectionsRecommendedPropertiesSection
				v-if="shouldShowSelectedProperties"
				:properties="selectedProperties?.properties ?? []"
				:cta-link="ctaLink"
				:is-loading="isLoadingSelectedProperties"
				container-class="bg-primary"
				@cta-view-more-clicked="
					(payload: ViewMoreClickedPayload) =>
						handleViewMoreClicked('selected properties', payload)
				"
			/>
		</ClientOnly>

		<div class="cv-auto cv-600">
			<LazyVisibleSectionsPilarFeaturesSection hydrate-on-visible />
		</div>

		<div class="cv-auto cv-400 bg-primary">
			<div class="flex flex-col">
				<div
					role="separator"
					class="my-1 h-[0.0625rem] bg-primary-light-4"
				></div>
				<LazyVisibleSectionsRegionsSection hydrate-on-visible />
				<div
					role="separator"
					class="my-1 h-[0.0625rem] bg-primary-light-4"
				></div>
			</div>
		</div>

		<div class="cv-auto cv-620">
			<LazyVisibleSectionsExclusivePropertiesSection
				hydrate-on-visible
				:properties="exclusiveProperties?.properties ?? []"
				:is-loading="isLoadingExclusiveProperties"
				title-variant="default"
			/>
		</div>

		<div class="cv-auto cv-700">
			<LazyVisibleSectionsSelectedRegionsSection
				hydrate-on-visible
				@cta-view-more-clicked="
					(payload: ViewMoreClickedPayload) =>
						handleViewMoreClicked('selected regions', payload)
				"
				@carousel_neighborhood_clicked="handleCarouselNeighborhoodClicked"
			/>
		</div>

		<div class="cv-auto cv-500">
			<LazyVisibleSectionsCuratedListsSection hydrate-on-visible />
		</div>

		<div class="cv-auto cv-400">
			<LazyVisibleSectionsTestimonialsSection
				hydrate-on-visible
				:testimonials="testimonials"
				:variant="testimonialsSectionVariant"
			/>
		</div>

		<div class="cv-auto cv-300">
			<LazyVisibleSectionsNeighborhoodLinkingSection hydrate-on-visible />
		</div>
		<div class="cv-auto cv-300">
			<LazyVisibleSectionsPoiAddressLinkingSection hydrate-on-visible />
		</div>
	</div>
</template>
